import { useCallback, useState } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import SectionTitle from 'src/components/ui/SectionTitle';
import BlockTitle from 'src/components/ui/BlockTitle';
import PageLoader from 'src/components/ui/PageLoader';
import EmptyState from 'src/components/ui/EmptyState';
import TextField from 'src/components/ui/TextField';
import AddCourseButton from 'src/components/course/AddCourseButton';
import RouterButton from 'src/components/ui/RouterButton';
import IconButton from 'src/components/ui/IconButton';
import CoursesView, { ViewType } from 'src/components/ui/CoursesView';
import BookIcon from 'src/assets/icons/book-open-2.svg';
import SearchIcon from 'src/assets/icons/search.svg';
import CloseIcon from 'src/assets/icons/close.svg';
import ContentIcon from 'src/assets/icons/layout-content.svg';
import ModuleIcon from 'src/assets/icons/layout-module.svg';
import ExternalIcon from 'src/assets/icons/share-external-link-1.svg';
import useAccessCodesEffect, {
  AccessCodesSchema,
} from 'src/hooks/useAccessCodesEffect';
import useCoursesList from 'src/hooks/useCoursesList';
import { getStateFromStorage, saveStateToStorage } from 'src/utils';

const DashboardPage = () => {
  const { accessCodes } = Route.useSearch();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');

  const [viewType, setViewType] = useState(() => {
    const saved = getStateFromStorage('VIEW', 'COURSES') as ViewType;
    return saved || ViewType.List;
  });

  const onViewChange = useCallback((type: ViewType) => {
    setViewType(type);
    saveStateToStorage('VIEW', 'COURSES', type);
  }, []);

  const {
    courses: allCourses,
    loading,
    refetch,
  } = useCoursesList({
    expired: false,
    skip: Boolean(accessCodes?.length),
    filter,
  });

  const currentCourse = allCourses.find(
    (c) => c.progress > 0 && !c.isCompleted
  );

  const restCourses = allCourses.filter(
    (c) =>
      Number(c.courseId) !== Number(currentCourse?.courseId) && !c.isCompleted
  );

  const completedCourses = allCourses.filter((c) => c.isCompleted);

  const onCourseAdd = useCallback(async () => refetch(), [refetch]);

  const { loading: codesLoading } = useAccessCodesEffect(accessCodes, () =>
    navigate({ to: '/', replace: true })
  );

  if (loading || codesLoading) return <PageLoader />;

  return (
    <>
      <SectionTitle title="Dashboard" className="px-6 py-2.5">
        <AddCourseButton onSuccess={onCourseAdd}>Add Course</AddCourseButton>
      </SectionTitle>
      <div className="bg-surface-alt rounded-3xl shadow-3 px-2 pt-4 pb-6">
        <div className="flex items-center justify-between gap-1.5 m-3">
          <SearchField filter={filter} setFilter={setFilter} />
          <IconButton
            onPress={() => onViewChange(ViewType.List)}
            className={
              viewType === ViewType.List ? 'bg-surface-alt-ghost' : undefined
            }
            Icon={ContentIcon}
            aria-label="List View"
            size="small"
          />
          <IconButton
            onPress={() => onViewChange(ViewType.Grid)}
            className={
              viewType === ViewType.Grid ? 'bg-surface-alt-ghost' : undefined
            }
            Icon={ModuleIcon}
            aria-label="Grid View"
            size="small"
          />
        </div>

        {allCourses.length > 0 ? (
          <>
            {currentCourse?.courseId && currentCourse.progress > 0 && (
              <>
                <BlockTitle variant="v2" className="px-4 mb-4">
                  Currently Studying
                </BlockTitle>
                <CoursesView
                  viewType={ViewType.List}
                  list={[currentCourse]}
                  firstActive
                />
              </>
            )}

            {restCourses.length > 0 && (
              <>
                <BlockTitle variant="v2" className="mt-8 mb-4 px-4">
                  My Courses
                </BlockTitle>
                <CoursesView viewType={viewType} list={restCourses} />
              </>
            )}

            {completedCourses.length > 0 && (
              <>
                <BlockTitle variant="v2" className="mt-8 mb-4 px-4">
                  Completed Courses
                </BlockTitle>
                <CoursesView viewType={viewType} list={completedCourses} />
              </>
            )}
          </>
        ) : (
          <EmptyState
            message={filter.length > 0 ? 'No courses found!' : NO_COURSES_ADDED}
            className="text-center px-10 min-h-[25rem]"
          >
            {filter.length === 0 && (
              <AddCourseButton
                IconLeft={BookIcon}
                className="mt-4"
                onSuccess={onCourseAdd}
              >
                Add Course
              </AddCourseButton>
            )}
          </EmptyState>
        )}

        <div className="text-center mt-8">
          <RouterButton
            to="/expired-courses"
            buttonProps={{
              variant: 'surface',
              IconLeft: ExternalIcon,
              ghost: true,
            }}
          >
            Expired Courses
          </RouterButton>
        </div>
      </div>
    </>
  );
};

const SearchField = ({
  filter,
  setFilter,
}: {
  filter: string;
  setFilter: (val: string) => void;
}) => {
  const [active, setActive] = useState(false);

  const onToggle = useCallback(
    () =>
      setActive((currActive) => {
        if (currActive) setFilter('');
        return !currActive;
      }),
    [setFilter]
  );

  return (
    <div className="min-w-0 flex items-center flex-1 lg:pr-4">
      <div
        className={`transition-all ${active ? 'w-full opacity-100 -mr-8' : 'opacity-0 w-0'}`}
      >
        {active && (
          <TextField
            // The user opened search; move focus into the newly revealed field.
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            aria-label="Filter Courses"
            size="small"
            placeholder="Search for a Course Title..."
            value={filter}
            onChange={(value) => setFilter(value)}
            onBlur={() => filter.length === 0 && setActive(false)}
          />
        )}
      </div>
      <IconButton
        size="small"
        className={`z-40 ${active ? 'h-7 -translate-x-0.5 rounded-r-md' : ''}`}
        Icon={active ? CloseIcon : SearchIcon}
        onPress={onToggle}
        aria-label={active ? 'Hide Search' : 'Show Search'}
      />
    </div>
  );
};

const NO_COURSES_ADDED =
  'You have no courses added to your account. Add course using the button below or reach out to your faculty for assistance.';

export const Route = createFileRoute(
  '/_authenticated/_layout-advanced/dashboard'
)({
  validateSearch: (search) => AccessCodesSchema.parse(search),
  beforeLoad: async ({ search }) => {
    const { success } = AccessCodesSchema.safeParse(search);

    if (!success) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/dashboard',
      });
    }
  },
  component: DashboardPage,
});
